import { useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useGamification } from '@/hooks/useGamification';
import { TaskCard, StreakBadge, XPProgress, RewardCelebration, ReferralCard } from '@/components/gamification';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CalendarCheck, UserPlus, Target, Sparkles } from 'lucide-react';

export default function Tasks() {
    const { status, loading, claimTask, completeTask } = useGamification();
    const [processingTask, setProcessingTask] = useState<string | null>(null);
    const [celebration, setCelebration] = useState<{
        isOpen: boolean;
        xpEarned: number;
        creditsEarned: number;
        leveledUp: boolean;
        newLevel?: number;
        taskTitle: string;
    }>({
        isOpen: false,
        xpEarned: 0,
        creditsEarned: 0,
        leveledUp: false,
        taskTitle: '',
    });

    const handleClaimTask = useCallback(async (taskSlug: string) => {
        setProcessingTask(taskSlug);
        await claimTask(taskSlug);
        setProcessingTask(null);
    }, [claimTask]);

    const handleCompleteTask = useCallback(async (taskSlug: string, taskTitle: string) => {
        setProcessingTask(taskSlug);
        const result = await completeTask(taskSlug);
        setProcessingTask(null);

        if (result.success) {
            setCelebration({
                isOpen: true,
                xpEarned: result.xp_earned || 0,
                creditsEarned: result.credits_earned || 0,
                leveledUp: result.leveled_up || false,
                newLevel: result.new_level,
                taskTitle,
            });
        }
    }, [completeTask]);

    const closeCelebration = useCallback(() => {
        setCelebration(prev => ({ ...prev, isOpen: false }));
    }, []);

    // Group tasks by category
    const tasksByCategory = status?.tasks.reduce((acc, task) => {
        // Infer category from task_type
        let category = 'general';
        if (task.task_type.includes('daily') || task.task_type.includes('login')) {
            category = 'daily';
        } else if (task.task_type.includes('referral') || task.task_type.includes('first-referral')) {
            category = 'referral';
        } else if (task.task_type.includes('streak') || task.task_type.includes('profile') || task.task_type.includes('publi')) {
            category = 'engagement';
        }

        if (!acc[category]) acc[category] = [];
        acc[category].push(task);
        return acc;
    }, {} as Record<string, typeof status.tasks>) || {};

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-32" />
                    <Skeleton className="h-24" />
                    <Skeleton className="h-48" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold">Suas Missões</h1>
                    <p className="text-muted-foreground">
                        Complete tarefas para ganhar XP e recompensas
                    </p>
                </div>

                {/* Stats Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* XP Progress */}
                    {status && (
                        <XPProgress
                            level={status.xp.level}
                            levelTitle={status.xp.level_title}
                            currentXP={status.xp.total}
                            nextLevelXP={status.xp.next_level_xp}
                        />
                    )}

                    {/* Streak */}
                    {status && (
                        <StreakBadge
                            current={status.streak.current}
                            multiplier={status.streak.multiplier}
                        />
                    )}
                </div>

                {/* Referral Section */}
                <ReferralCard />

                {/* Tasks Tabs */}
                <Tabs defaultValue="all" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="all" className="flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            <span className="hidden sm:inline">Todas</span>
                        </TabsTrigger>
                        <TabsTrigger value="daily" className="flex items-center gap-2">
                            <CalendarCheck className="w-4 h-4" />
                            <span className="hidden sm:inline">Diárias</span>
                        </TabsTrigger>
                        <TabsTrigger value="referral" className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4" />
                            <span className="hidden sm:inline">Indicações</span>
                        </TabsTrigger>
                        <TabsTrigger value="engagement" className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4" />
                            <span className="hidden sm:inline">Conquistas</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="all" className="mt-4 space-y-3">
                        {status?.tasks.length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                Nenhuma missão disponível no momento
                            </p>
                        ) : (
                            status?.tasks.map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    onClaim={() => handleClaimTask(task.task_type)}
                                    onComplete={() => handleCompleteTask(task.task_type, task.title)}
                                    isLoading={processingTask === task.task_type}
                                />
                            ))
                        )}
                    </TabsContent>

                    <TabsContent value="daily" className="mt-4 space-y-3">
                        {(tasksByCategory['daily'] || []).length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                Nenhuma missão diária disponível
                            </p>
                        ) : (
                            tasksByCategory['daily']?.map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    onClaim={() => handleClaimTask(task.task_type)}
                                    onComplete={() => handleCompleteTask(task.task_type, task.title)}
                                    isLoading={processingTask === task.task_type}
                                />
                            ))
                        )}
                    </TabsContent>

                    <TabsContent value="referral" className="mt-4 space-y-3">
                        {(tasksByCategory['referral'] || []).length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                Nenhuma missão de indicação disponível
                            </p>
                        ) : (
                            tasksByCategory['referral']?.map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    onClaim={() => handleClaimTask(task.task_type)}
                                    onComplete={() => handleCompleteTask(task.task_type, task.title)}
                                    isLoading={processingTask === task.task_type}
                                />
                            ))
                        )}
                    </TabsContent>

                    <TabsContent value="engagement" className="mt-4 space-y-3">
                        {(tasksByCategory['engagement'] || []).length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                Nenhuma conquista disponível
                            </p>
                        ) : (
                            tasksByCategory['engagement']?.map((task) => (
                                <TaskCard
                                    key={task.id}
                                    task={task}
                                    onClaim={() => handleClaimTask(task.task_type)}
                                    onComplete={() => handleCompleteTask(task.task_type, task.title)}
                                    isLoading={processingTask === task.task_type}
                                />
                            ))
                        )}
                    </TabsContent>
                </Tabs>

                {/* Celebration Modal */}
                <RewardCelebration
                    isOpen={celebration.isOpen}
                    onClose={closeCelebration}
                    xpEarned={celebration.xpEarned}
                    creditsEarned={celebration.creditsEarned}
                    leveledUp={celebration.leveledUp}
                    newLevel={celebration.newLevel}
                    taskTitle={celebration.taskTitle}
                />
            </div>
        </DashboardLayout>
    );
}
